import { useCallback, useEffect, useMemo } from 'react'
import { useResourceMutations } from '@/components/admin/shared/hooks/use-resource'
import type { ProblemType } from '../../../../db/schema/enums.js'
import type { ProblemTagSlug } from '../../../../lib/constants/problem-tags.js'
import { trpc } from '../../../../lib/trpc.js'
import type { CoachingQuestion, QuestionApproach } from '../types'

const PAGE_SIZE = 500

interface QuestionFormData {
  externalId?: string
  name: string
  difficulty?: string | null
  premiumRequired?: boolean
  type?: ProblemType
  problemTagSlugs?: string[]
  acceptanceRate?: number | null
  freqBar?: number | null
  totalAccepted?: number | null
  totalSubmitted?: number | null
  companies?: string[]
  url?: string
  editorialUrl?: string
  source?: string
  sourceIcon?: string
  approaches?: QuestionApproach[]
}

/**
 * Update a single question in the tRPC cache.
 * Called from problem editor after saving to keep cache in sync.
 *
 * NOTE: This is a no-op stub. With tRPC, cache invalidation happens
 * automatically via the mutations in useCoachingQuestions().
 * Consumers that import this can continue to call it harmlessly.
 */
export function updateQuestionInCache(
  _updatedQuestion: CoachingQuestion,
): void {
  // No-op: tRPC React Query cache is managed via invalidation in mutations.
}

/**
 * Add a new question to the tRPC cache.
 * Called from problem editor after creating a new problem.
 *
 * NOTE: This is a no-op stub. With tRPC, cache invalidation happens
 * automatically via the mutations in useCoachingQuestions().
 * Consumers that import this can continue to call it harmlessly.
 */
export function addQuestionToCache(_newQuestion: CoachingQuestion): void {
  // No-op: tRPC React Query cache is managed via invalidation in mutations.
}

export function useCoachingQuestions() {
  const utils = trpc.useUtils()

  // Infinite query: fetches all questions page by page
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: queryError,
  } = trpc.admin.questions.list.useInfiniteQuery(
    { limit: PAGE_SIZE },
    {
      getNextPageParam: (lastPage) => {
        // The list procedure returns { data, pagination } when limit is set
        if ('pagination' in lastPage && lastPage.pagination.nextCursor) {
          return lastPage.pagination.nextCursor
        }
        return undefined
      },
    },
  )

  // Auto-fetch remaining pages. Runs in an effect (commit phase), never in
  // the render body: firing fetchNextPage() during render re-triggers on every
  // unrelated re-render of any consumer, layering redundant paginated requests.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Flatten all pages into a single questions array
  const questions = useMemo<CoachingQuestion[]>(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => {
      if ('data' in page) return page.data
      // If returned without pagination wrapper (no limit case)
      return page
    })
  }, [data])

  // Total count from the first page's pagination metadata
  const totalCount = useMemo<number | null>(() => {
    if (!data?.pages?.length) return null
    const firstPage = data.pages[0]
    if ('pagination' in firstPage) return firstPage.pagination.total
    return questions.length
  }, [data, questions.length])

  // Derived: unique sources
  const sources = useMemo(() => {
    const sourceSet = new Set<string>()
    questions.forEach((q) => {
      if (q.allSources?.length) {
        for (const s of q.allSources) sourceSet.add(s.source)
      } else if (q.source) {
        sourceSet.add(q.source)
      }
    })
    return Array.from(sourceSet).sort()
  }, [questions])

  // Derived: unique approach tags — the palette ApproachesModal offers when
  // tagging an approach (Approach.tags). Sourced from tags already used across
  // approaches; LG-355 dropped Problem.tags, which this list previously used,
  // and the 8-slug problem-descriptor vocabulary is the wrong grain here.
  const tags = useMemo(() => {
    const tagSet = new Set<string>()
    questions.forEach((q) => {
      q.approaches?.forEach((a) => {
        a.tags?.forEach((t) => {
          tagSet.add(t)
        })
      })
    })
    return Array.from(tagSet).sort()
  }, [questions])

  // Mutations. The list is paginated via useInfiniteQuery above, so only the
  // write half of the shared resource shape applies here.
  const invalidate = useCallback(() => {
    utils.admin.questions.list.invalidate()
  }, [utils])

  const { create, update, remove } = useResourceMutations({
    create: trpc.admin.questions.create,
    update: trpc.admin.questions.update,
    remove: trpc.admin.questions.delete,
    invalidate,
  })

  const addQuestion = async (
    questionData: QuestionFormData,
  ): Promise<CoachingQuestion> => {
    const {
      difficulty,
      acceptanceRate,
      freqBar,
      totalAccepted,
      totalSubmitted,
      ...rest
    } = questionData
    const result = await create({
      ...rest,
      difficulty: difficulty ?? undefined,
      acceptanceRate: acceptanceRate ?? undefined,
      freqBar: freqBar ?? undefined,
      totalAccepted: totalAccepted ?? undefined,
      totalSubmitted: totalSubmitted ?? undefined,
      // Client form carries slugs as plain strings (sourced from
      // PROBLEM_TAG_VOCABULARY); the router validates them against the
      // real enum, so narrow here rather than widening the form contract.
      problemTagSlugs: rest.problemTagSlugs as ProblemTagSlug[] | undefined,
    })
    // The create endpoint returns { id }, not a full CoachingQuestion.
    // Return a minimal object; the list will be invalidated and refetched.
    return { id: result.id } as CoachingQuestion
  }

  const updateQuestion = async (
    id: string,
    questionData: Partial<QuestionFormData>,
  ): Promise<CoachingQuestion> => {
    const result = await update({
      id,
      ...questionData,
      problemTagSlugs: questionData.problemTagSlugs as
        | ProblemTagSlug[]
        | undefined,
    })
    // The update endpoint returns { id }, not a full CoachingQuestion.
    return { id: result.id } as CoachingQuestion
  }

  const deleteQuestion = async (id: string): Promise<void> => {
    await remove({ id })
  }

  const refresh = async () => {
    await utils.admin.questions.list.invalidate()
  }

  return {
    questions,
    sources,
    tags,
    loading: isLoading,
    loadingMore: isFetchingNextPage || (hasNextPage ?? false),
    totalCount,
    error: queryError?.message ?? null,
    refresh,
    addQuestion,
    updateQuestion,
    deleteQuestion,
  }
}
